import { useState, useCallback } from 'react';
import { FilterConditionOutput } from '@shared/components/ui/AdvancedFilter';

interface BankAccountsFiltersState {
  status: string[];
  country: string;
}

export const useBankAccountsFilters = ({
  initialConditions,
  onChange,
}: {
  initialConditions: FilterConditionOutput[];
  onChange?: (conditions: FilterConditionOutput[]) => void;
}) => {
  const parseConditions = (conditions: FilterConditionOutput[]): BankAccountsFiltersState => {
    const state: BankAccountsFiltersState = {
      status: [],
      country: '',
    };

    conditions.forEach((condition) => {
      if (condition.column === 'status') {
        if (condition.action === 'in' && Array.isArray(condition.value)) {
          state.status = condition.value as string[];
        } else if (condition.action === 'equals') {
          state.status = [condition.value as string];
        }
      }
      if (condition.column === 'country_code' && condition.action === 'contains') {
        state.country = condition.value as string;
      }
    });

    return state;
  };

  const [filters, setFilters] = useState<BankAccountsFiltersState>(() => parseConditions(initialConditions));

  const convertToConditions = useCallback((state: BankAccountsFiltersState) => {
    const conditions: FilterConditionOutput[] = [];

    if (state.status.length > 0) {
      conditions.push({
        column: 'status',
        action: 'in',
        value: state.status,
      });
    }

    if (state.country) {
      conditions.push({
        column: 'country_code',
        action: 'contains',
        value: state.country,
      });
    }

    return conditions;
  }, []);

  const handleFilterChange = (key: keyof BankAccountsFiltersState, value: any) => {
    setFilters((prev) => {
      const next = { ...prev, [key]: value };
      onChange?.(convertToConditions(next));
      return next;
    });
  };

  const clearFilters = () => {
    const next = {
      status: [],
      country: '',
    };
    setFilters(next);
    onChange?.(convertToConditions(next));
  };

  return {
    filters,
    handleFilterChange,
    clearFilters,
  };
};
